import React, { useEffect, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAutomationEditorContext } from '../AutomationEditorContext';
import { useScrollIndicator } from '../useScrollIndicator';
import { useSegmentDefinitions } from '../useSegmentDefinitions';
import { StepDialogFooter } from '../StepDialogFooter';
import { ExcludeSegmentPanel } from '../segment-picker/ExcludeSegmentPanel';
import { SegmentOptionList } from '../segment-picker/SegmentOptionList';
import { SegmentPickerTabs, type SegmentPickerTab } from '../segment-picker/SegmentPickerTabs';
import { getSegmentPickerPanelId, getSegmentPickerTabId } from '../segment-picker/segment-picker-ids';
import { SegmentReuseWarning } from '../segment-picker/SegmentReuseWarning';
import { SEGMENT_USAGE_TAGS } from '../segment-picker/segment-picker-copy';
import {
  getTemplateSegmentUsage,
  isTriggerStepUsage,
  omitSegmentUsages,
  type SegmentUsage,
} from '../segment-picker/segment-usage';
import { SegmentListStates } from './SegmentListStates';
import { DATE_TRIGGER_STEP_COPY } from './date-trigger-step-copy';
import {
  DATE_AUDIENCE_SINGLE_SELECT_NOTE,
  DATE_TRIGGER_AUDIENCE_DESCRIPTION,
  DATE_TRIGGER_TAB_LABEL,
} from './trigger-exclusions';
import styles from './DateTriggerConfigDialog.module.css';

const AUDIENCE_USAGE: SegmentUsage = { kind: 'audience' };

interface DateTriggerAudienceSelectionStepProps {
  selectedAudienceId: string;
  onSelect: (audienceId: string) => void;
  excludedIds: string[];
  onExcludedChange: (excludedIds: string[]) => void;
  onBack: () => void;
  onCancel: () => void;
  onDone: () => void;
}

export const DateTriggerAudienceSelectionStep: React.FC<DateTriggerAudienceSelectionStepProps> = ({
  selectedAudienceId,
  onSelect,
  excludedIds,
  onExcludedChange,
  onBack,
  onCancel,
  onDone,
}) => {
  const { editor } = useAutomationEditorContext();
  const { data: segmentDefinitions, isLoading, error } = useSegmentDefinitions();
  const { mainRef, hasMoreBelow, onScroll, checkScroll } = useScrollIndicator();
  const [activeTab, setActiveTab] = useState<SegmentPickerTab>('include');
  const pickerId = useId();

  const segments = segmentDefinitions ?? [];
  const hasSegments = segments.length > 0;
  const usedElsewhere = omitSegmentUsages(
    getTemplateSegmentUsage(editor.state.localContents),
    isTriggerStepUsage,
  );
  const selectedAudienceIds = selectedAudienceId ? [selectedAudienceId] : [];
  const selectedAudience = segments.find(segment => segment.canonicalVersionId === selectedAudienceId);

  useEffect(() => {
    checkScroll();
  }, [segmentDefinitions, activeTab, checkScroll]);

  return (
    <>
      <SegmentPickerTabs
        pickerId={pickerId}
        activeTab={activeTab}
        onChange={setActiveTab}
        includeLabel={DATE_TRIGGER_TAB_LABEL}
        exclusionCount={excludedIds.length}
      />
      <div className={styles.mainWrapper}>
        <main
          ref={mainRef}
          onScroll={onScroll}
          className={styles.audienceList}
          id={getSegmentPickerPanelId(pickerId)}
          role="tabpanel"
          aria-labelledby={getSegmentPickerTabId(pickerId, activeTab)}>
          <SegmentListStates isLoading={isLoading} error={error} isEmpty={!hasSegments} />
          {hasSegments && activeTab === 'include' && (
            <>
              <p className={styles.audienceIntro}>{DATE_TRIGGER_STEP_COPY.audience.listIntro}</p>
              <p className={styles.audienceIntro}>{DATE_AUDIENCE_SINGLE_SELECT_NOTE}</p>
              {selectedAudienceId && (
                <SegmentReuseWarning
                  segmentName={selectedAudience?.name}
                  pickedUsage={AUDIENCE_USAGE}
                  usagesElsewhere={usedElsewhere.get(selectedAudienceId)}
                />
              )}
              <SegmentOptionList
                segments={segments}
                selectedIds={selectedAudienceIds}
                onSelect={onSelect}
                usedElsewhere={usedElsewhere}
                blocked={{ segmentIds: excludedIds, tag: SEGMENT_USAGE_TAGS.exclusion }}
              />
            </>
          )}
          {hasSegments && activeTab === 'exclude' && (
            <ExcludeSegmentPanel
              segments={segments}
              excludedIds={excludedIds}
              onChange={onExcludedChange}
              includedId={selectedAudienceId || null}
              includedTag={SEGMENT_USAGE_TAGS.audience}
              audienceDescription={DATE_TRIGGER_AUDIENCE_DESCRIPTION}
              usedElsewhere={usedElsewhere}
            />
          )}
        </main>
        {hasMoreBelow && (
          <div className={styles.scrollIndicator}>
            <span className={styles.scrollIndicatorPill}>
              <ChevronDown size={12} />
              More below
            </span>
          </div>
        )}
      </div>
      <StepDialogFooter
        onBack={onBack}
        onCancel={onCancel}
        onOK={onDone}
        okLabel={DATE_TRIGGER_STEP_COPY.audience.doneLabel}
      />
    </>
  );
};
